import React, { useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Grid,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import LoginIcon from "@mui/icons-material/Login";
import GoogleIcon from "@mui/icons-material/Google";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import {
  businessInitialValues,
  BusinessSchema,
} from "../../validation/account.validation";
import {
  createBusiness,
  sendVerificationEmail,
  verifyEmailOTP,
} from "../../services/account.service";
import { businessMessages } from "../../constants/message.constant";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import logo from "../../assets/Logo.jpg";
import backgroundImg from "../../assets/background-img.jpeg";
import { useLocation, useNavigate } from "react-router-dom";
import slugify from "slugify";
import API from "../../constants/api.constant";

const BusinessRegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const socialVerified = queryParams.get("socialVerified") === "true";
  const socialEmail = queryParams.get("socialEmail") || "";
  const socialFirstName = queryParams.get("socialFirstName") || "";
  const socialLastName = queryParams.get("socialLastName") || "";
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [emailVerified, setEmailVerified] = useState(socialVerified && Boolean(socialEmail));
  const [verifiedEmail, setVerifiedEmail] = useState(socialVerified ? socialEmail : "");

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const startSocialRegistration = (provider: "google" | "linkedin") => {
    const params = new URLSearchParams({
      mode: "business",
      returnPath: location.pathname,
    });
    window.location.href = `${API.API_BASE_URL}/account/social-register/${provider}?${params.toString()}`;
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Main Content */}
      <Box
        flex={1}
        sx={{
          background: `url(${backgroundImg}) no-repeat center center`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 2,
          width: "100%",
          backgroundSize: "cover",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: 570,
            borderRadius: 4,
            p: { xs: 3, sm: 3 },
            mx: 2,
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.10)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{ width: 165, height: 165, objectFit: "contain" }}
          />
          <Typography
            variant="h6"
            fontWeight={700}
            align="center"
            sx={{ mb: 1, letterSpacing: 0.5 }}
          >
            Create Business Account
          </Typography>
          <Formik
            initialValues={{
              ...businessInitialValues,
              email: socialEmail,
              ownerFirstName: socialFirstName,
              ownerLastName: socialLastName,
            }}
            enableReinitialize
            validationSchema={BusinessSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                const businessSlug = slugify(values.name, { lower: true });
                const data = {...values, businessSlug }
                await createBusiness(data);
                setSnackbar({
                  open: true,
                  message: businessMessages.businessRegistered,
                  severity: "success",
                });
                resetForm();
                navigate("/login");
              } catch (error: any) {
                setSnackbar({
                  open: true,
                  message:
                    error?.message ||
                    error?.response?.data?.message ||
                    businessMessages.businessRegistrationFailed,
                  severity: "error",
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
            }) => (
              <Form style={{ width: "100%" }}>
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<GoogleIcon />}
                        disabled={isSubmitting}
                        sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
                        onClick={() => startSocialRegistration("google")}
                      >
                        Register with Google
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<LinkedInIcon />}
                        disabled={isSubmitting}
                        sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
                        onClick={() => startSocialRegistration("linkedin")}
                      >
                        Register with LinkedIn
                      </Button>
                    </Grid>
                  </Grid>
                  {/* Business Name & EIN in one row on desktop, stacked on mobile/tablet */}
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <TextField
                        label="Business Name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: {
                            borderRadius: 10,
                          },
                        }}
                        placeholder="Enter business name"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Owner First Name"
                        name="ownerFirstName"
                        value={values.ownerFirstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.ownerFirstName && Boolean(errors.ownerFirstName)}
                        helperText={touched.ownerFirstName && errors.ownerFirstName}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: {
                            borderRadius: 10,
                          },
                        }}
                        placeholder="First Name"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Owner Middle Name"
                        name="ownerMiddleName"
                        value={values.ownerMiddleName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.ownerMiddleName && Boolean(errors.ownerMiddleName)}
                        helperText={touched.ownerMiddleName && errors.ownerMiddleName}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: {
                            borderRadius: 10,
                          },
                        }}
                        placeholder="Middle Name"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Owner Last Name"
                        name="ownerLastName"
                        value={values.ownerLastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.ownerLastName && Boolean(errors.ownerLastName)}
                        helperText={touched.ownerLastName && errors.ownerLastName}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: {
                            borderRadius: 10,
                          },
                        }}
                        placeholder="Last Name"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="EIN"
                        name="ein"
                        value={values.ein}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.ein && Boolean(errors.ein)}
                        helperText={touched.ein && errors.ein}
                        fullWidth
                        inputProps={{ maxLength: 15 }}
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: {
                            borderRadius: 10,
                          },
                        }}
                        placeholder="Enter EIN"
                      />
                    </Grid>
                  {/* Phone & Email in one row on desktop, stacked on mobile/tablet */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Phone"
                        name="phone"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                        fullWidth
                        inputProps={{ maxLength: 15 }}
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: {
                            borderRadius: 10,
                          },
                        }}
                        placeholder="Enter phone"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Email"
                        name="email"
                        value={values.email}
                        onChange={(event) => {
                          handleChange(event);
                          setEmailVerified(false);
                          setVerifiedEmail("");
                          setOtp("");
                        }}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={
                          emailVerified && verifiedEmail === values.email
                            ? "Email verified"
                            : touched.email && errors.email
                        }
                        fullWidth
                        disabled={isSubmitting || emailVerified}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: {
                            borderRadius: 10,
                          },
                        }}
                        placeholder="Enter email"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        disabled={
                          isSubmitting ||
                          isSendingOtp ||
                          !values.email ||
                          Boolean(errors.email) ||
                          (emailVerified && verifiedEmail === values.email)
                        }
                        sx={{
                          borderRadius: 2,
                          py: 1.75,
                          fontWeight: 600,
                        }}
                        onClick={async () => {
                          try {
                            setIsSendingOtp(true);
                            await sendVerificationEmail(values.email);
                            setSnackbar({
                              open: true,
                              message: "Verification OTP sent to your email",
                              severity: "success",
                            });
                          } catch (error: any) {
                            setSnackbar({
                              open: true,
                              message: error?.message || "Failed to send verification OTP",
                              severity: "error",
                            });
                          } finally {
                            setIsSendingOtp(false);
                          }
                        }}
                      >
                        {isSendingOtp ? "Sending..." : "Send Email OTP"}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Email OTP"
                        value={otp}
                        onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 4))}
                        fullWidth
                        disabled={isSubmitting || emailVerified}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: {
                            borderRadius: 10,
                          },
                        }}
                        placeholder="Enter 4 digit OTP"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={
                          isSubmitting ||
                          isVerifyingOtp ||
                          !values.email ||
                          otp.length !== 4 ||
                          (emailVerified && verifiedEmail === values.email)
                        }
                        sx={{
                          borderRadius: 2,
                          py: 1.75,
                          fontWeight: 600,
                        }}
                        onClick={async () => {
                          try {
                            setIsVerifyingOtp(true);
                            await verifyEmailOTP(values.email, otp);
                            setEmailVerified(true);
                            setVerifiedEmail(values.email);
                            setSnackbar({
                              open: true,
                              message: "Email verified successfully",
                              severity: "success",
                            });
                          } catch (error: any) {
                            setSnackbar({
                              open: true,
                              message: error?.message || "Invalid or expired OTP",
                              severity: "error",
                            });
                          } finally {
                            setIsVerifyingOtp(false);
                          }
                        }}
                      >
                        {isVerifyingOtp ? "Verifying..." : "Verify Email"}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Website"
                      name="website"
                      value={values.website}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.website && Boolean(errors.website)}
                      helperText={touched.website && errors.website}
                      fullWidth
                      disabled={isSubmitting}
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        style: {
                          borderRadius: 10,
                        },
                      }}
                      placeholder="Enter website"
                    />
                    </Grid>
                  </Grid>
                  {/* Synonyms */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, textAlign: "left", fontWeight: 500 }}
                    >
                      Doing Business  As(DBA)
                    </Typography>
                    <FieldArray name="synonyms">
                      {({ push, remove }) => (
                        <>
                          {values.synonyms.map((synonym, idx) => (
                            <Box
                              key={idx}
                              display="flex"
                              alignItems="center"
                              mb={1}
                            >
                              <TextField
                                name={`synonyms[${idx}]`}
                                value={synonym}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                  touched.synonyms &&
                                  Array.isArray(touched.synonyms) &&
                                  touched.synonyms[idx] &&
                                  Boolean(
                                    errors.synonyms && errors.synonyms[idx]
                                  )
                                }
                                helperText={
                                  touched.synonyms &&
                                  Array.isArray(touched.synonyms) &&
                                  touched.synonyms[idx] &&
                                  errors.synonyms &&
                                  errors.synonyms[idx]
                                }
                                fullWidth
                                size="small"
                                disabled={isSubmitting}
                                variant="outlined"
                                InputProps={{
                                  style: {
                                    minHeight: 48,
                                    borderRadius: 10,
                                  },
                                }}
                                placeholder={`DBA ${idx + 1}`}
                              />
                              {values.synonyms.length > 1 && (
                                <Tooltip title="Remove" arrow>
                                  <IconButton
                                    aria-label="remove synonym"
                                    onClick={() => remove(idx)}
                                    disabled={isSubmitting}
                                    size="small"
                                    sx={{ ml: 1 }}
                                  >
                                    <RemoveCircleOutlineIcon color="error" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          ))}
                          <Button
                            variant="text"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => push("")}
                            disabled={
                              isSubmitting || values.synonyms.length >= 5
                            }
                            sx={{
                              mt: 1,
                              fontWeight: 500,
                              textTransform: "none",
                              fontSize: "0.95rem",
                            }}
                            fullWidth
                          >
                            Add DBA
                          </Button>
                          {errors.synonyms &&
                            typeof errors.synonyms === "string" && (
                              <Typography color="error" variant="caption">
                                {errors.synonyms}
                              </Typography>
                            )}
                        </>
                      )}
                    </FieldArray>
                  </Box>
                  <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Street"
                    name="street"
                    value={values.street}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.street && Boolean(errors.street)}
                    helperText={touched.street && errors.street}
                    fullWidth
                    disabled={isSubmitting}
                    variant="outlined"
                    size="medium"
                    InputProps={{
                      style: {
                        borderRadius: 10,
                      },
                    }}
                    placeholder="Enter street address"
                  />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="City"
                    name="city"
                    value={values.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.city && Boolean(errors.city)}
                    helperText={touched.city && errors.city}
                    fullWidth
                    disabled={isSubmitting}
                    variant="outlined"
                    size="medium"
                    InputProps={{
                      style: {
                        borderRadius: 10,
                      },
                    }}
                    placeholder="Enter city"
                  />
                  </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="State"
                    name="state"
                    value={values.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.state && Boolean(errors.state)}
                    helperText={touched.state && errors.state}
                    fullWidth
                    disabled={isSubmitting}
                    variant="outlined"
                    size="medium"
                    InputProps={{
                      style: {
                        borderRadius: 10,
                      },
                    }}
                    placeholder="Enter state"
                  />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Zipcode"
                    name="zipcode"
                    value={values.zipcode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.zipcode && Boolean(errors.zipcode)}
                    helperText={touched.zipcode && errors.zipcode}
                    fullWidth
                    disabled={isSubmitting}
                    variant="outlined"
                    size="medium"
                    InputProps={{
                      style: {
                        borderRadius: 10,
                      },
                    }}
                    placeholder="Enter zipcode"
                  />
                  </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: { borderRadius: 10 },
                          endAdornment: (
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword((show) => !show)}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        }}
                        placeholder="Enter password"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.confirmPassword &&
                          Boolean(errors.confirmPassword)
                        }
                        helperText={
                          touched.confirmPassword && errors.confirmPassword
                        }
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: { borderRadius: 10 },
                          endAdornment: (
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={() =>
                                setShowConfirmPassword((show) => !show)
                              }
                              edge="end"
                              size="small"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        }}
                        placeholder="Re-enter password"
                      />
                    </Grid>
                  </Grid>
                  {/* Buttons in one row */}
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || !emailVerified || verifiedEmail !== values.email}
                        fullWidth
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: "1rem",
                          mt: 1,
                        }}
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <BusinessIcon />
                          )
                        }
                      >
                        {isSubmitting ? "Creating..." : "Create Business"}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<LoginIcon />}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: "1rem",
                          mt: 1,
                        }}
                        disabled={isSubmitting}
                        onClick={() => {
                          navigate("/login");
                        }}
                      >
                        Login
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </Form>
            )}
          </Formik>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Paper>
      </Box>
    </Box>
  );
};

export default BusinessRegisterForm;
